import type { ParamDef, ParamValue } from './types'

export type CoerceResult = { ok: true; value: ParamValue } | { ok: false; error: string }

/**
 * Instant, client-side feedback only. The server (or the mock engine) stays
 * the authority and re-validates on send. `raw` is what the control holds: a
 * string for text inputs/selects, a boolean for On/Off.
 */
export function coerceParam(def: ParamDef, raw: ParamValue): CoerceResult {
  switch (def.kind) {
    case 'bool':
      if (typeof raw === 'boolean') return { ok: true, value: raw }
      return { ok: true, value: raw === 'true' }
    case 'int':
    case 'float': {
      if (typeof raw === 'number') {
        if (!Number.isFinite(raw)) return { ok: false, error: 'Enter a number' }
        if (def.kind === 'int' && !Number.isInteger(raw)) return { ok: false, error: 'Whole number only' }
        return { ok: true, value: raw }
      }
      const text = String(raw).trim()
      if (text === '' || !Number.isFinite(Number(text))) return { ok: false, error: 'Enter a number' }
      const n = Number(text)
      if (def.kind === 'int' && !Number.isInteger(n)) return { ok: false, error: 'Whole number only' }
      return { ok: true, value: n }
    }
    case 'choice': {
      const text = String(raw)
      if (!def.options.includes(text)) return { ok: false, error: 'Pick one of the options' }
      return { ok: true, value: text }
    }
    case 'str':
      return { ok: true, value: String(raw) }
  }
}

export interface CoerceAllResult {
  values: Record<string, ParamValue>
  /** Field name to message; empty when every field is valid. */
  errors: Record<string, string>
}

/** Coerces every field of a form. Fields absent from `raw` fall back to their default. */
export function coerceAll(defs: ParamDef[], raw: Record<string, ParamValue>): CoerceAllResult {
  const values: Record<string, ParamValue> = {}
  const errors: Record<string, string> = {}
  for (const def of defs) {
    const input = raw[def.name] ?? def.default
    if (input === null || input === undefined) continue
    const result = coerceParam(def, input)
    if (result.ok) values[def.name] = result.value
    else errors[def.name] = result.error
  }
  return { values, errors }
}
